import type { SyntheticEvent } from 'react';
import React, { useState } from 'react';
import { useTranslation } from '@whoop/i18n/lang/client';
import { AmplitudeEvents } from 'whoop-analytics/lib/types';
import { useShallow } from 'zustand/react/shallow';
import { usePathname } from 'next/navigation';
import { Loading } from 'ui';
import LoadingButton from 'ui/components/ButtonV2/LoadingButton';
import { PlanTypes } from 'ui/utils/constants/maverick';
import {
  hasProductTypeInCartItemGroups,
  getMembershipInCart,
} from '../cart.utils';
import KeepShoppingButton from './keepShoppingButton';
import { useAnalytics } from '@/hooks/analytics';
import { useNavigation } from '@/hooks/navigation';
import { useGetCurrentPageMaverick } from '@/hooks/getCurrentPage';
import {
  JOIN_FLOW_STEPS,
  ProductType,
  UPGRADE_STEPS,
} from '@/lib/constants/maverick';
import { useSiteStore } from '@/store/SiteStoreProvider';
import {
  getAccessoriesPageUrl,
  getOrderPageUrl,
} from '@/lib/utils/navigationHelpers';
import { getIsTypeFromAttributes } from '@/lib/utils/attributeHelper';
import { EXPERIMENT_UPCYCLE_MODAL_ADD_GIFT_KIT } from '@/lib/constants/experiments';

export type CartButtonVariants =
  | 'continueToCheckout'
  | 'control'
  | 'preferCheckout'
  | 'preferAccessories'
  | 'chooseUpgradeOption'
  | 'chooseUpcycleOption';

export interface CartCTAButtonsProps {
  onCartClose: () => void;
  variant: CartButtonVariants;
  showTrialBlock: boolean;
  showOnlyPrimaryButton?: boolean;
  skipMemSelectFlow?: string;
  isUpcycleExperimentVariant?: boolean;
}

export function CartCTAButtons({
  onCartClose,
  variant,
  showTrialBlock,
  showOnlyPrimaryButton = false,
  skipMemSelectFlow,
  isUpcycleExperimentVariant,
}: CartCTAButtonsProps): React.JSX.Element {
  const { t } = useTranslation('cart');
  const { trackAmplitudeEvent } = useAnalytics();
  const { goTo } = useNavigation();
  const pathname = usePathname();
  const { cart } = useSiteStore(
    useShallow((state) => ({
      cart: state.cart,
    })),
  );

  const [isPrimaryButtonLoading, setIsPrimaryButtonLoading] = useState(false);
  const [isSecondaryButtonLoading, setIsSecondaryButtonLoading] =
    useState(false);

  const page = useGetCurrentPageMaverick();
  const isUpgradeFlow = pathname.includes('upgrade');
  const isLandingPage = page === JOIN_FLOW_STEPS.JOIN;
  const isBandsPage = page === JOIN_FLOW_STEPS.BANDS;
  const isFamilyPage = page === JOIN_FLOW_STEPS.FAMILY;
  const isMembershipPage = page === JOIN_FLOW_STEPS.MEMBERSHIP;
  const isAccessoriesPage =
    page === JOIN_FLOW_STEPS.ACCESSORIES || page === UPGRADE_STEPS.ACCESSORIES;
  const isUpgradeOptionsPage = page === UPGRADE_STEPS.OPTIONS;
  const isUpgradeUpcyclePage = page === UPGRADE_STEPS.UPCYCLE;

  // Check if trial membership is in cart
  const membershipInCart = getMembershipInCart(cart.cartItemGroups);
  const hasTrialInCart = membershipInCart
    ? getIsTypeFromAttributes(membershipInCart.attributes, PlanTypes.TRIAL)
    : false;

  /* Override Passed in Variant
   * If trial membership in cart and trial block experiment is on, variant should be 'prefer checkout'.
   * Otherwise, if trial membership in cart, variant should be 'continueToCheckout'
   * -- This will prioritize checkout over accessories
   * If skip membership select flow, variant should be 'preferCheckout'
   * -- This will show the primary button as checkout and secondary button as accessories
   * If on upcycle page and user has upcycle membership in cart, variant should be 'control'
   * -- This will show the primary button as Continue and will move the user to accessories page
   * -- Default is to show choose upcycle option
   */
  let buttonsVariant = variant;
  if (hasTrialInCart && isMembershipPage) {
    buttonsVariant = showTrialBlock ? 'preferCheckout' : 'continueToCheckout';
  } else if (skipMemSelectFlow) {
    buttonsVariant = 'preferCheckout';
  } else if (
    isUpgradeUpcyclePage &&
    (hasProductTypeInCartItemGroups(
      cart.cartItemGroups,
      ProductType.UPCYCLE_MEMBERSHIP,
    ) ||
      isUpcycleExperimentVariant)
  ) {
    buttonsVariant = 'control';
  }

  const onContinueToCheckout = (): void => {
    trackAmplitudeEvent(AmplitudeEvents.CloseCart, {
      CTA: 'Check Out',
    });
    const url = getOrderPageUrl({
      isUpgradeFlow,
      cartItemGroups: cart.cartItemGroups,
    });
    goTo(url);
  };

  const onContinueToAccessories = (): void => {
    trackAmplitudeEvent(AmplitudeEvents.CloseCart, {
      CTA: buttonsVariant === 'control' ? 'Continue' : 'Add Accessories',
    });
    const url = getAccessoriesPageUrl({
      isUpgradeFlow,
      cartItemGroups: cart.cartItemGroups,
    });
    goTo(url);
  };

  const primaryButtonActionVariants = {
    continueToCheckout: onContinueToCheckout,
    control: onContinueToAccessories,
    preferCheckout: onContinueToCheckout,
    preferAccessories: onContinueToAccessories,
    chooseUpgradeOption: onCartClose,
    chooseUpcycleOption: onCartClose,
  };

  const secondaryButtonActionVariants: Partial<
    Record<CartButtonVariants, () => void>
  > = {
    preferCheckout: onContinueToAccessories,
    preferAccessories: onContinueToCheckout,
  };

  const clickHandler = (
    event: SyntheticEvent,
    onClickAction?: () => void,
  ): void => {
    event.stopPropagation();
    if (onClickAction) onClickAction();
  };

  const primaryButtonLabelVariants = {
    continueToCheckout: t('continue'),
    control: t('continue'),
    preferCheckout: t('checkout'),
    preferAccessories: t('addAccessories'),
    chooseUpgradeOption: t('chooseUpgradeOption'),
    chooseUpcycleOption: t('chooseUpcycleOption'),
  };

  const { primaryButtonLabel, primaryButtonAction } =
    isLandingPage ||
    isMembershipPage ||
    isFamilyPage ||
    isBandsPage ||
    isUpgradeOptionsPage ||
    isUpgradeUpcyclePage
      ? {
          primaryButtonLabel: primaryButtonLabelVariants[buttonsVariant],
          primaryButtonAction: primaryButtonActionVariants[buttonsVariant],
        }
      : {
          primaryButtonLabel: t('continueToCheckout'),
          primaryButtonAction: onContinueToCheckout,
        };

  const secondaryButtonLabelVariants: Partial<
    Record<CartButtonVariants, string>
  > = {
    preferCheckout: t('addAccessories'),
    preferAccessories: t('checkout'),
  };

  return (
    <div className='flex flex-col gap-2.5'>
      <LoadingButton
        data-testid='cart-continueButton'
        disabled={isPrimaryButtonLoading || isSecondaryButtonLoading}
        onClick={async (event) => {
          setIsPrimaryButtonLoading(true);
          clickHandler(event, primaryButtonAction);
        }}
        size='medium'
        type='button'
        variant='secondary'
      >
        {isPrimaryButtonLoading ? (
          <Loading className='absolute p-1 opacity-100' />
        ) : null}
        {primaryButtonLabel}
      </LoadingButton>
      {!showOnlyPrimaryButton && (
        <>
          {(isLandingPage ||
            isMembershipPage ||
            isFamilyPage ||
            isBandsPage ||
            isUpgradeUpcyclePage) &&
          secondaryButtonLabelVariants[buttonsVariant] &&
          secondaryButtonActionVariants[buttonsVariant] ? (
            <LoadingButton
              data-testid='cart-secondaryContinueButton'
              disabled={isPrimaryButtonLoading || isSecondaryButtonLoading}
              onClick={async (event) => {
                setIsSecondaryButtonLoading(true);
                clickHandler(
                  event,
                  secondaryButtonActionVariants[buttonsVariant],
                );
                setIsSecondaryButtonLoading(false);
              }}
              size='medium'
              type='button'
              variant='primary'
            >
              {secondaryButtonLabelVariants[buttonsVariant]}
            </LoadingButton>
          ) : null}
          {isAccessoriesPage ? (
            <KeepShoppingButton
              onClick={(event) => clickHandler(event, onCartClose)}
            >
              {t('keepShopping')}
            </KeepShoppingButton>
          ) : null}
        </>
      )}
    </div>
  );
}
